// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { stringify } from 'qs';
import {
  QxMetric,
  QxMetricApi,
  QxMetricsResponse,
  QxMetricsQuery,
  QxMetricsAPIResponse,
  WidgetCount,
  DetectedQsl,
} from '../types';
import { cleanUpParams } from '../util';

const { REACT_APP_DASHING_API_URL, REACT_APP_GRYPHON_API_URL } = process.env;

// Define a service using a base URL and expected endpoints
export const dashingApi = createApi({
  reducerPath: 'dashingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_DASHING_API_URL}/`,
  }),
  endpoints: (builder) => ({
    getQxMetrics: builder.query<QxMetricsResponse, QxMetricsQuery>({
      query: (parameters) => {
        const params = cleanUpParams(
          {
            ...parameters,
            offset: (parameters.page || 0) * (parameters.limit || 0),
          },
          false
        );
        return `/qxvs/?${stringify(params, { indices: false })}`;
      },
      transformResponse: (response: QxMetricsAPIResponse): QxMetricsResponse => {
        return {
          ...response,
          qxvs: response.qxvs.map(formatQxvItem),
        };
      },
    }),
  }),
});

const formatQxvItem = (qxvItem: QxMetricApi): QxMetric => {
  const {
    authored_language,
    used_locales,
    authored_language_match_qsl_language,
    authored_language_overriden,
    locales_metadata,
    csv_calls,
    widget_count,
    detected_qsl_languages,
    mixed_qsl_languages,
    ...questionnaireDetailsMapped
  } = qxvItem.questionnaire_details;

  const interludeContentMapped = {
    resource_calls: qxvItem.call_activity.resource_calls,
    panman_calls: qxvItem.call_activity.panman_calls,
    csv_calls: qxvItem.questionnaire_details.csv_calls,
    history_calls: qxvItem.call_activity.history_calls,
    panman_assign_treatment_count: qxvItem.questionnaire_details.panman_assign_treatment_count,
  };

  const pdlContentMapped = {
    pdl_updates: qxvItem.call_activity.pdl_updates,
    pdl_updates_guarded: qxvItem.call_activity.pdl_updates_guarded,
    pdl_updates_unguarded: qxvItem.call_activity.pdl_updates_unguarded,
    pdl_updates_if_0: qxvItem.call_activity.pdl_updates_if_0,
    pdl_sets: qxvItem.call_activity.pdl_sets,
  };

  const responseActivityMapped = {
    ...qxvItem.responses_activity,
    named_response_list_count: qxvItem.questionnaire_details.named_response_list_count,
    object_sets: qxvItem.questionnaire_details.object_sets,
  };

  const exitCountsData: any = qxvItem.questionnaire_details.exit_counts;
  let exit_counts_by_status: any = {};
  let exit_counts_by_option: any = {};
  Object.keys(exitCountsData).forEach((key: string) => {
    if (key.includes('exit_by_status_')) {
      exit_counts_by_status[key.substring('exit_by_status_'.length)] = exitCountsData[key];
    } else if (key.includes('exit_by_option_')) {
      exit_counts_by_option[key.substring('exit_by_option_'.length)] = exitCountsData[key];
    }
  });
  const exitCountsMapped = { exit_counts_by_status, exit_counts_by_option };

  const isErrorRow = (): boolean => {
    return (
      qxvItem.responses_activity.responses_100_499 > 3 ||
      qxvItem.responses_activity.responses_500_999 > 1 ||
      qxvItem.responses_activity.responses_1000_plus > 1 ||
      qxvItem.subquestions.subquestions_50_99 > 5 ||
      qxvItem.subquestions.subquestions_100_499 > 1 ||
      qxvItem.subquestions.subquestions_500_999 > 0 ||
      qxvItem.subquestions.subquestions_1000_plus > 0
    );
  };

  // @ts-ignore
  return {
    ...qxvItem,
    questionnaire_details: {
      ...questionnaireDetailsMapped,
      widget_count: sortData(widget_count),
    },
    interlude_content: interludeContentMapped,
    pdl_content: pdlContentMapped,
    exit_counts: exitCountsMapped,
    responses_activity: responseActivityMapped,
    languages: {
      authored_language: sortData(authored_language),
      used_locales: sortData(used_locales),
      authored_language_match_qsl_language,
      authored_language_overriden,
      locales_metadata,
      detected_qsl_languages: sortData(detected_qsl_languages),
      mixed_qsl_languages,
    },
    errorRow: isErrorRow(),
    admin_url: formatAdminUrl(qxvItem.admin_url),
  };
};

const formatAdminUrl = (url: string): string => {
  if (!url) return url;
  var splits = url.split('/#');
  splits[0] =
    splits.length > 1 && REACT_APP_GRYPHON_API_URL ? REACT_APP_GRYPHON_API_URL : splits[0];
  return splits.join('/#');
};

const sortData = (data: string[] | DetectedQsl | WidgetCount): any => {
  if (Array.isArray(data) && data.every((i) => typeof i === 'string')) {
    return data.sort();
  } else if (typeof data === 'object') {
    return Object.keys(data)
      .sort()
      .map((key) => {
        return { [key]: (data as Record<string, number>)[key] };
      });
  } else {
    return data;
  }
};

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetQxMetricsQuery } = dashingApi;
